import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  DragOverlay,
} from "@dnd-kit/core";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { Info, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, extractApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { canCreateOrEditCRM, canSeeArchivedCRM } from "../lib/crm-permissions";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { DEAL_STAGE_ORDER, DEAL_STAGE_LABELS, normalizeDealStageForWorkflow } from "../lib/labels";
import { formatMoney } from "../lib/format";
import StatusPill from "../components/status/StatusPill";
import DealFormDialog from "../components/deals/DealFormDialog";
import { calculatePipelineForecast } from "../lib/pipeline-forecast";
import { calculateDealTcv, formatRevenueSummary } from "../lib/deal-revenue";
import { replaceDealById } from "../lib/pipeline-deals-state";
import { getProbabilityMeta } from "../lib/probability-meta";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

export default function PipelinePage() {
  const navigate = useNavigate();
  const { role, isSuperAdmin } = useAuth();
  const canCreate = canCreateOrEditCRM({ role, isSuperAdmin });
  const canSeeArchived = canSeeArchivedCRM({ role, isSuperAdmin });
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDeal, setActiveDeal] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/api/deals", {
        params: showArchived && canSeeArchived ? { include_archived: true } : {},
      });
      setDeals(r.data);
    } catch (e) {
      toast.error(extractApiError(e, "Pipeline konnte nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, [showArchived, canSeeArchived]);
  useEffect(() => {
    load();
  }, [load]);

  const byStage = useMemo(() => {
    const map = {};
    DEAL_STAGE_ORDER.forEach((s) => (map[s] = []));
    deals.forEach((d) => {
      const visibleStage = normalizeDealStageForWorkflow(d.stage);
      if (!map[visibleStage]) map[visibleStage] = [];
      map[visibleStage].push(d);
    });
    return map;
  }, [deals]);

  const forecast = useMemo(() => calculatePipelineForecast(deals), [deals]);

  const handleDragStart = (e) => {
    const id = e.active.id;
    const d = deals.find((x) => x.id === id);
    setActiveDeal(d || null);
  };

  const handleDragEnd = async (e) => {
    setActiveDeal(null);
    const { active, over } = e;
    if (!over) return;
    const overId = String(over.id);
    const overStage = overId.startsWith("col:") ? overId.slice(4) : deals.find((d) => d.id === overId)?.stage;
    if (!overStage) return;
    const dealId = active.id;
    const target = deals.find((d) => d.id === dealId);
    if (!target || target.stage === overStage) return;

    const prevStage = target.stage;
    setDeals((prev) => prev.map((d) => (d.id === dealId ? { ...d, stage: overStage } : d)));
    try {
      const r = await api.post(`/api/deals/${dealId}/stage`, { stage: overStage });
      if (r?.data) setDeals((prev) => replaceDealById(prev, r.data));
      toast.success(`Verschoben nach „${DEAL_STAGE_LABELS[overStage]}“`);
    } catch (err) {
      setDeals((prev) => prev.map((d) => (d.id === dealId ? { ...d, stage: prevStage } : d)));
      toast.error(extractApiError(err, "Verschieben fehlgeschlagen."));
    }
  };

  return (
    <div>
      <PageHeader
        title="Pipeline"
        description="Deals per Drag & Drop zwischen Deal-Phasen verschieben."
        actions={
          <div className="flex items-center gap-3">
            {canSeeArchived ? (
              <label className="inline-flex select-none items-center gap-2 rounded-full border border-border/60 bg-background/70 px-2.5 py-1.5 text-sm text-foreground shadow-sm">
                <Switch
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                  className="data-[state=checked]:border-sky-300 data-[state=checked]:bg-sky-500 dark:data-[state=checked]:border-sky-200 dark:data-[state=checked]:bg-sky-400 data-[state=unchecked]:border-slate-400/80 data-[state=unchecked]:bg-slate-300 dark:data-[state=unchecked]:border-slate-500/80 dark:data-[state=unchecked]:bg-slate-700 data-[state=checked]:shadow-[0_0_0_1px_rgba(255,255,255,0.18),0_0_16px_rgba(59,130,246,0.22)] dark:data-[state=checked]:shadow-[0_0_0_1px_rgba(255,255,255,0.14),0_0_18px_rgba(56,189,248,0.28)] data-[state=unchecked]:shadow-[0_0_0_1px_rgba(15,23,42,0.08)] dark:data-[state=unchecked]:shadow-[0_0_0_1px_rgba(255,255,255,0.06)] [&>span]:bg-white dark:[&>span]:bg-slate-100 data-[state=checked]:[&>span]:bg-white dark:data-[state=checked]:[&>span]:bg-sky-950"
                />
                <span>Archivierte Deals anzeigen</span>
              </label>
            ) : null}
            {canCreate ? (
              <Button onClick={() => setDialog(true)} data-testid="deal-create-button">
                <Plus className="w-4 h-4" /> Deal erstellen
              </Button>
            ) : null}
          </div>
        }
      />

      <TooltipProvider delayDuration={150}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <KpiCard
            label="Pipeline-Wert"
            tooltip="Summe der TCV-Werte aller aktuell sichtbaren Deals."
            value={formatMoney(forecast.totalTcv)}
          />
          <KpiCard
            label="Gewichteter Forecast"
            tooltip="Summe aus TCV × Abschlusswahrscheinlichkeit je sichtbarem Deal."
            value={formatMoney(forecast.weightedTcv)}
          />
          <KpiCard
            label="Deals"
            tooltip="Anzahl der aktuell sichtbaren Deals in der Pipeline."
            value={forecast.dealCount}
          />
          <KpiCard
            label="Ø Wahrscheinlichkeit"
            tooltip="Einfacher Durchschnitt der Abschlusswahrscheinlichkeiten aller sichtbaren Deals."
            value={`${Math.round(forecast.averageProbability)} %`}
          />
        </div>
      </TooltipProvider>

      {loading ? (
        <div className="text-sm text-muted-foreground py-12 text-center">
          Pipeline wird geladen …
        </div>
      ) : (
        <div data-testid="pipeline-kanban" className="overflow-x-auto kanban-scroll pb-4 pl-1 pr-6 md:pr-10">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="flex gap-3 min-w-max">
              {DEAL_STAGE_ORDER.map((stage) => (
                <Column
                  key={stage}
                  stage={stage}
                  deals={byStage[stage] || []}
                  onOpenDeal={(dealId) => navigate(`/deals/${dealId}`)}
                />
              ))}
            </div>
            <DragOverlay>{activeDeal ? <DealCardView deal={activeDeal} dragging /> : null}</DragOverlay>
          </DndContext>
        </div>
      )}

      <DealFormDialog
        open={dialog}
        onOpenChange={setDialog}
        onSaved={() => {
          setDialog(false);
          load();
        }}
      />
    </div>
  );
}

function KpiCard({ label, tooltip, value }) {
  return (
    <div className="obsidian p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        <span
          className="min-w-0 hyphens-auto break-words [overflow-wrap:anywhere]"
          lang="de"
        >
          {label}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground/80 hover:text-foreground"
              aria-label={`${label} erklären`}
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs border border-border/60 bg-popover text-popover-foreground shadow-lg break-words [overflow-wrap:anywhere]">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Column({ stage, deals, onOpenDeal }) {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${stage}` });
  const total = deals.reduce((sum, d) => sum + calculateDealTcv(d), 0);
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-column-${stage}`}
      className={`w-[300px] shrink-0 obsidian flex flex-col transition-shadow duration-200 ${
        isOver ? "brand-glow-strong" : ""
      }`}
    >
      <div
        className="px-3 py-3 border-b border-border"
      >
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {DEAL_STAGE_LABELS[stage] || stage}
            </div>
            <div className="text-sm font-medium mt-0.5">
              {deals.length} {deals.length === 1 ? "Deal" : "Deals"}
            </div>
          </div>
          <div className="text-xs tabular-nums text-muted-foreground">{formatMoney(total)}</div>
        </div>
        <div
          className="h-0.5 mt-2.5 rounded-full"
          style={{ background: "color-mix(in srgb, var(--brand) 70%, transparent)" }}
        />
      </div>
      <div className="p-2 flex-1 space-y-2 min-h-[140px]">
        {deals.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-8 border border-dashed border-border rounded-lg">
            Keine Deals
          </div>
        )}
        {deals.map((d) => (
          <DealCard key={d.id} deal={d} onOpenDeal={onOpenDeal} />
        ))}
      </div>
    </div>
  );
}

function DealCard({ deal, onOpenDeal }) {
  // IMPORTANT: when using DragOverlay, do NOT apply transform to the original
  // node. The overlay handles the floating card position; applying transform
  // here causes the visible card to move twice / offset from the cursor.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: deal.id,
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`kanban-card-${deal.id}`}
      className={`obsidian obsidian-hover p-3 cursor-grab active:cursor-grabbing transition-opacity duration-150 ${
        isDragging ? "opacity-30" : deal.archived_at ? "opacity-70" : ""
      }`}
    >
      <DealCardView deal={deal} onOpenDeal={onOpenDeal} />
    </div>
  );
}

function DealCardView({ deal, dragging = false, onOpenDeal = null }) {
  const probability = getProbabilityMeta(deal.probability);
  return (
    <div className={dragging ? "kanban-card-dragging obsidian p-3" : ""}>
      {deal.archived_at ? (
        <div className="mb-2">
          <StatusPill tone="muted">Archiviert</StatusPill>
        </div>
      ) : null}
      {dragging || !onOpenDeal ? (
        <div className="text-sm font-medium leading-snug break-words hyphens-auto [overflow-wrap:anywhere]" lang="de">
          {deal.title}
        </div>
      ) : (
        <button
          type="button"
          className="text-sm font-medium leading-snug text-left hover:underline break-words hyphens-auto [overflow-wrap:anywhere]"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onOpenDeal(deal.id);
          }}
          lang="de"
        >
          {deal.title}
        </button>
      )}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="tabular-nums text-muted-foreground">{formatMoney(calculateDealTcv(deal))}</span>
        <span className="text-muted-foreground">{formatRevenueSummary(deal)}</span>
        <StatusPill tone={probability.tone}>{probability.label}</StatusPill>
      </div>
    </div>
  );
}
